import { gql } from '@apollo/client';
import type { OrderBilling, OrderLineItem } from './getOrders';

/**
 * Single WooCommerce order (WPGraphQL WooCommerce).
 * idType: DATABASE_ID so we can pass the numeric order id from the URL.
 * Field set aligned with GET_ORDERS (avoids null global ids on some orders).
 */
export const GET_ORDER = gql`
  query GetOrder($id: ID!, $idType: OrderIdTypeEnum) {
    order(id: $id, idType: $idType) {
      databaseId
      orderNumber
      date
      status
      total
      currency
      paymentMethodTitle
      transactionId
      customer {
        databaseId
        firstName
        lastName
        email
      }
      billing {
        firstName
        lastName
        email
        phone
        address1
        address2
        city
        state
        postcode
        country
      }
      shipping {
        firstName
        lastName
        address1
        address2
        city
        state
        postcode
        country
      }
      lineItems {
        nodes {
          productId
          variationId
          quantity
          subtotal
          total
          product {
            node {
              name
              sku
            }
          }
          metaData {
            key
            value
          }
        }
      }
      feeLines {
        nodes {
          name
          amount
        }
      }
      shippingLines {
        nodes {
          methodTitle
          total
        }
      }
      couponLines {
        nodes {
          code
          discount
        }
      }
      metaData {
        key
        value
      }
    }
  }
`;

export interface OrderShipping {
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

export interface OrderLineItemDetail extends OrderLineItem {
  variationId?: number;
  subtotal?: string;
}

export interface SingleOrder {
  id?: string;
  databaseId: number;
  orderNumber?: string;
  date?: string;
  status?: string;
  total?: string;
  currency?: string;
  paymentMethodTitle?: string;
  transactionId?: string;
  customer?: {
    databaseId?: number;
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
  billing?: OrderBilling | null;
  shipping?: OrderShipping | null;
  lineItems?: {
    nodes: OrderLineItemDetail[];
  };
  feeLines?: { nodes: Array<{ id?: string; name?: string; amount?: string }> };
  shippingLines?: { nodes: Array<{ id?: string; methodTitle?: string; total?: string }> };
  couponLines?: { nodes: Array<{ id?: string; code?: string; discount?: string }> };
  metaData?: Array<{ key: string; value: string }>;
}

export interface GetOrderData {
  order: SingleOrder | null;
}

export interface GetOrderVariables {
  id: string;
  idType?: 'DATABASE_ID' | 'ID';
}
